/**
 * SEO Component - Comprehensive meta tags for SEO and AI Search
 * Uses native DOM manipulation to avoid Helmet issues
 */
import { useEffect } from 'react';

const SITE_NAME = 'UniXperts';
const SITE_URL = 'https://unixperts.com';
const DEFAULT_IMAGE = 'https://customer-assets.emergentagent.com/job_e561d373-9c90-4225-a893-129d3a1abc0e/artifacts/ricl3iso_Unixperts%20Logo.png';
const TWITTER_HANDLE = '@unixperts';

/**
 * SEO Component - Updates document meta tags
 */
const SEO = ({
  title = '',
  description = '',
  image = DEFAULT_IMAGE,
  type = 'website',
  keywords = '',
  noindex = false,
  canonical = '',
  structuredData = null
}) => {
  const pageTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} - Your Gateway to Global Education`;
  const metaDescription = description || 'UniXperts is your trusted partner for studying abroad. Get expert guidance for university admissions in USA, UK, Canada, Australia & more.';
  const canonicalUrl = canonical || (typeof window !== 'undefined' ? window.location.href.split('?')[0] : SITE_URL);

  useEffect(() => {
    // Update document title
    document.title = pageTitle;

    // Helper to update or create meta tag
    const updateMeta = (name, content, property = false) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector);
      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Helper to update or create link tag
    const updateLink = (rel, href) => {
      let link = document.querySelector(`link[rel="${rel}"]`);
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };

    // Basic meta tags
    updateMeta('description', metaDescription);
    if (keywords) updateMeta('keywords', keywords);
    updateMeta('robots', noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1');
    
    // Canonical URL (critical for SEO)
    updateLink('canonical', canonicalUrl);

    // OpenGraph tags
    updateMeta('og:site_name', SITE_NAME, true);
    updateMeta('og:title', pageTitle, true);
    updateMeta('og:description', metaDescription, true);
    updateMeta('og:type', type, true);
    updateMeta('og:image', image, true);
    updateMeta('og:url', canonicalUrl, true);
    updateMeta('og:locale', 'en_IN', true);

    // Twitter tags
    updateMeta('twitter:card', 'summary_large_image');
    updateMeta('twitter:site', TWITTER_HANDLE);
    updateMeta('twitter:title', pageTitle);
    updateMeta('twitter:description', metaDescription);
    updateMeta('twitter:image', image);

    // Add structured data if provided
    if (structuredData) {
      // Remove existing structured data
      const existingScript = document.querySelector('script[data-seo-structured]');
      if (existingScript) {
        existingScript.remove();
      }
      
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-structured', 'true');
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }

  }, [pageTitle, metaDescription, keywords, noindex, type, image, canonicalUrl, structuredData]);

  // This component doesn't render anything visible
  return null;
};

export default SEO;
